/**
 * Test Runs screen (epic 031 task 06; ADR-010 §2 row `screens/test-runs/**`).
 *
 * One surface over the test harness: pick a campaign (grouped by style), run
 * an **admin-mode** test (co-op / custom opt-in lead-post) or a **CTO** test
 * (pick a link, enter an email, launch in a new tab), watch the run live
 * (2 s polling), read every attempt, see the test address to hand to partners
 * for deletion, the activation-gate state and the campaign's **Test mode**
 * toggle. The selected campaign rides the route (`/test-runs/:campaignId?`);
 * the selected run is component state.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ActivationCheck, TestRunDetail, TestRunSummary } from '@leads-platform/shared';
import { normalizeCrmConfig } from '@leads-platform/shared';
import { ApiError, type CampaignDetailResponse, type CampaignLinkRow } from '../../lib/api';
import { PageHeader } from '../../components/ui/page-header';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../components/ui/toast';
import { AdminRunPanel } from './admin-run-panel';
import { CampaignPicker } from './campaign-picker';
import { CtoRunPanel } from './cto-run-panel';
import { GateCard } from './gate-card';
import { PageRunPanel } from './page-run-panel';
import { RunDetail } from './run-detail';
import { RunList } from './run-list';
import {
  activationCheck,
  getCampaigns,
  getLinks,
  getRun,
  listRuns,
  setTestMode,
} from './test-runs.api';
import { useRunPolling } from './use-run-polling';

export function TestRuns() {
  const { campaignId } = useParams<{ campaignId?: string }>();
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();

  const [campaigns, setCampaigns] = useState<CampaignDetailResponse[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);

  const [runs, setRuns] = useState<TestRunSummary[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [check, setCheck] = useState<ActivationCheck | null>(null);
  const [links, setLinks] = useState<CampaignLinkRow[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TestRunDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Campaigns — reference data for the picker, loaded once.
  useEffect(() => {
    getCampaigns()
      .then((rows) => {
        setCampaigns(rows);
        setCampaignsError(null);
      })
      .catch((err: ApiError) => setCampaignsError(err.message))
      .finally(() => setCampaignsLoading(false));
  }, []);

  const campaign =
    campaignId === undefined ? null : (campaigns.find((c) => c.id === campaignId) ?? null);

  // Per-campaign data: runs + activation check; links for Email CTO campaigns.
  useEffect(() => {
    setRuns([]);
    setCheck(null);
    setLinks([]);
    setSelectedRunId(null);
    setDetail(null);
    setDetailError(null);
    if (!campaignId) return;

    let cancelled = false;
    setRunsLoading(true);
    listRuns(campaignId)
      .then((list) => {
        if (!cancelled) setRuns(list.rows);
      })
      .catch((err: ApiError) => {
        if (!cancelled) toastError(err.message || 'Loading test runs failed');
      })
      .finally(() => {
        if (!cancelled) setRunsLoading(false);
      });
    activationCheck(campaignId)
      .then((result) => {
        if (!cancelled) setCheck(result);
      })
      .catch(() => {
        // The gate card keeps its loading text — not fatal to the screen.
      });
    if (campaign && campaign.campaignStyle === 'cto_email') {
      setLinksLoading(true);
      getLinks(campaignId)
        .then((rows) => {
          if (!cancelled) setLinks(rows);
        })
        .catch(() => {
          // The link select keeps its loading text — not fatal to the screen.
        })
        .finally(() => {
          if (!cancelled) setLinksLoading(false);
        });
    }
    return () => {
      cancelled = true;
    };
    // `campaign` is derived from `campaigns` + `campaignId`; the style check
    // re-runs with the id, and `campaigns` only settles once after mount.
  }, [campaignId, campaign?.campaignStyle]);

  const selectRun = useCallback(
    (runId: string) => {
      setSelectedRunId(runId);
      if (!campaignId) return;
      setDetailError(null);
      getRun(campaignId, runId)
        .then(setDetail)
        .catch((err: ApiError) => setDetailError(err.message || 'Loading the run failed'));
    },
    [campaignId]
  );

  const refetchRun = useCallback(() => {
    if (!campaignId || !selectedRunId) return;
    getRun(campaignId, selectedRunId)
      .then((next) => {
        setDetail(next);
        setDetailError(null);
      })
      .catch(() => {
        // Transient — the next tick retries.
      });
    // The list refreshes with the detail.
    listRuns(campaignId)
      .then((list) => setRuns(list.rows))
      .catch(() => {
        // Transient — the next tick retries.
      });
  }, [campaignId, selectedRunId]);

  const refreshGateAndRuns = useCallback(() => {
    if (!campaignId) return;
    listRuns(campaignId)
      .then((list) => setRuns(list.rows))
      .catch(() => {
        // The list stays stale — not fatal.
      });
    activationCheck(campaignId)
      .then(setCheck)
      .catch(() => {
        // The gate card keeps its previous state — not fatal.
      });
  }, [campaignId]);

  const handleCreated = useCallback(
    (run: TestRunSummary) => {
      success(run.mode === 'admin' ? 'Test run started' : 'Test link created');
      setSelectedRunId(run.id);
      refreshGateAndRuns();
      selectRun(run.id);
    },
    [refreshGateAndRuns, selectRun, success]
  );

  const handleTestModeChange = useCallback(
    (enabled: boolean) => {
      if (!campaign) return;
      setTestMode(campaign.id, enabled)
        .then(() => {
          success(enabled ? 'Test mode on — leads are test leads' : 'Test mode off');
          return getCampaigns().then(setCampaigns);
        })
        .catch((err: ApiError) => toastError(err.message || 'Changing test mode failed'));
    },
    [campaign, toastError, success]
  );

  // Live polling while the selected run is queued | running (2 s, 10 min cap).
  const { timedOut, refresh } = useRunPolling({
    runId: selectedRunId,
    status: detail?.run.status ?? null,
    refetch: refetchRun,
  });

  const selectCampaign = (id: string) => {
    navigate(`/test-runs/${id}`);
  };

  return (
    <div>
      <PageHeader
        kicker="Operations"
        title="Test Runs"
        desc="Confirm that the full co-op flow is working correctly before the campaign goes live, and launch CTO links exactly as a real user would. Test leads never count."
      />

      <Card className="mb-4">
        <CardContent>
          {campaignsError ? (
            <p className="text-sm text-destructive">Error loading campaigns: {campaignsError}</p>
          ) : campaignsLoading ? (
            <p className="text-sm text-faint">Loading campaigns…</p>
          ) : (
            <CampaignPicker
              campaigns={campaigns}
              selectedId={campaignId ?? null}
              onSelect={selectCampaign}
            />
          )}
        </CardContent>
      </Card>

      {campaignId && !campaign && !campaignsLoading && (
        <p className="text-sm text-muted">
          Campaign not found — it may have been deleted. Pick another campaign above.
        </p>
      )}

      {campaign && (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <GateCard
              check={check}
              campaignStatus={campaign.status}
              testModeEnabled={campaign.status === 'test'}
              onTestModeChange={handleTestModeChange}
            />
            {campaign.campaignStyle === 'paid_social' ? (
              <Card>
                <CardContent className="pt-6">
                  <p className="max-w-xl text-sm text-muted">
                    Paid Social pages are tested by putting the campaign in Test mode: every lead it
                    captures is a test lead.
                  </p>
                </CardContent>
              </Card>
            ) : campaign.campaignStyle === 'cto_email' ? (
              <CtoRunPanel
                campaign={campaign}
                links={links}
                linksLoading={linksLoading}
                onCreated={handleCreated}
              />
            ) : campaign.campaignStyle === 'custom_optin' ? (
              // Epic 100 D7: the Test page panel always; the admin (lead-post)
              // panel below it only when the campaign actually posts leads —
              // a click-out campaign has no admin run to make.
              <div className="space-y-4">
                <PageRunPanel campaign={campaign} onCreated={handleCreated} />
                {normalizeCrmConfig(campaign.crmConfig).send_mode !== 'click_out' && (
                  <AdminRunPanel
                    campaignId={campaign.id}
                    campaignName={campaign.name}
                    onCreated={handleCreated}
                  />
                )}
              </div>
            ) : (
              <AdminRunPanel
                campaignId={campaign.id}
                campaignName={campaign.name}
                onCreated={handleCreated}
              />
            )}
          </div>

          <Card className="overflow-hidden">
            <div className="flex items-center justify-between px-6 pt-6">
              <h2 className="text-lg font-semibold text-ink">Test runs</h2>
              {runsLoading && <span className="text-xs text-faint">Refreshing…</span>}
            </div>
            <CardContent className="px-0 pb-0">
              <RunList runs={runs} selectedRunId={selectedRunId} onSelect={selectRun} />
            </CardContent>
          </Card>

          {detailError && <p className="text-sm text-destructive">{detailError}</p>}
          {detail && detail.run.campaignId === campaign.id && (
            <RunDetail detail={detail} timedOut={timedOut} onRefresh={refresh} />
          )}
        </div>
      )}
    </div>
  );
}
